/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Alert, Snackbar } from "@mui/material";
import { getIconList } from "apis/visitors";
import { useCallback, useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";
import Button from "uis/Button";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import PurposePreview from "./purpose-preview";
import { getWalkInVisitPurpose, updateVisitPurpose } from "apis/visitors/setting/walk-in-visit";
import { TPurpose } from "apis/visitors/setting/type";

const Title = styled.p`
    color: ${theme.neutral500};
    font-size: 17px;
    font-weight: 600;
`;

export default function MeetingPurpose({ deviceId }: { deviceId: string }) {
    const [purposes, setPurposes] = useState<TPurpose[]>([]);
    const [success, setSuccess] = useState<boolean>(false);
    const dragItem = useRef<number>(0);
    const dragOverItem = useRef<number>(0);
    const [icons, setIcons] = useState<{ id: number; imageUrl: string }[]>([]);

    const dragStart = (e: any, position: number) => {
        dragItem.current = position;
    };

    const dragEnter = (e: any, position: number) => {
        dragOverItem.current = position;
    };

    const drop = (e: any) => {
        const temp = [...purposes];
        const dragItemValue = temp[dragItem.current];
        temp.splice(dragItem.current, 1);
        temp.splice(dragOverItem.current, 0, dragItemValue);
        dragItem.current = 0;
        dragOverItem.current = 0;

        setPurposes(temp);
    };

    const onHandleDelete = (index: number) => {
        setPurposes([...purposes].filter((item, idx) => index !== idx));
    };

    useEffect(() => {
        getWalkInVisitPurpose(deviceId).then((res) => {
            if (res) setPurposes(res);
        });
        getIconList().then((res) => {
            setIcons(res);
        });
    }, []);

    return (
        <div
            css={css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 60px;
                margin: 30px;
            `}
        >
            <Snackbar
                open={success}
                autoHideDuration={3000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity="success">설정이 업데이트 되었습니다.</Alert>
            </Snackbar>
            <div
                css={css`
                    flex: 1;
                    max-width: 500px;
                `}
            >
                <Title>방문 목적</Title>
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                        margin-top: 24px;
                    `}
                >
                    {purposes.map((item, index) => {
                        return (
                            <PurposeSetting
                                icons={icons}
                                key={index}
                                placeholder={item.content}
                                value={item.content}
                                image={item.image}
                                onChangeIcon={(value) => {
                                    const temp = [...purposes];
                                    temp[index].image = value;
                                    setPurposes(temp);
                                }}
                                onDragStart={(e) => dragStart(e, index)}
                                onDragEnter={(e) => dragEnter(e, index)}
                                drop={drop}
                                onDelete={() => onHandleDelete(index)}
                                onChangeTitle={(value) => {
                                    const temp = [...purposes];
                                    temp[index].content = value;
                                    setPurposes(temp);
                                }}
                            />
                        );
                    })}
                </div>
                {purposes.length < 9 ? (
                    <div
                        onClick={() => {
                            setPurposes((prev) => [...prev, {}]);
                        }}
                        css={css`
                            margin-top: 16px;
                            display: flex;
                            gap: 4px;
                            align-items: center;
                            margin-left: auto;
                            width: max-content;

                            cursor: pointer;
                        `}
                    >
                        <Icon name="plus-circle" color={theme.primary500} />
                        <p
                            css={css`
                                color: ${theme.primary500};
                                font-size: 16px;
                                font-weight: 600;

                                &:hover {
                                    font-weight: 800;
                                }
                            `}
                        >
                            추가하기
                        </p>
                    </div>
                ) : null}
                <Button
                    onClick={async () => {
                        const newPurposes: TPurpose[] = [...purposes]
                            .map((item, index) => {
                                if (!item.id) {
                                    return {
                                        id: item.id,
                                        displayOrder: index + 1,
                                        image: item.image,
                                        content: item.content,
                                    };
                                } else {
                                    return { ...item, displayOrder: index + 1 };
                                }
                            })
                            .filter((item) => item.content);

                        const res = await updateVisitPurpose(deviceId, newPurposes);
                        if (res) {
                            const result = await getWalkInVisitPurpose(deviceId);
                            if (result) setPurposes(result);
                            setSuccess(true);
                        }
                    }}
                    title="저장하기"
                    styles={{
                        container: css`
                            width: max-content;
                            padding: 12px 40px;
                            margin-left: auto;
                            margin-top: 24px;
                        `,
                    }}
                />
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                `}
            >
                <Title>미리보기</Title>
                <PurposePreview purposes={purposes} />
            </div>
        </div>
    );
}

const IconContainer = styled.div<{ image: string }>`
    background-image: url(${(props) => props.image});
    width: 24px;
    height: 24px;
    display: flex;
    background-size: contain;
    background-repeat: no-repeat;
`;

const IconWrapper = styled.div`
    padding: 4px;
    border-radius: 4px;
    &:hover {
        background-color: ${theme.neutral50};
    }
`;

const PurposeSetting = ({
    placeholder,
    image,
    onDragEnter,
    onDragStart,
    drop,
    onDelete,
    onChangeTitle = () => {},
    value,
    icons,
    onChangeIcon = () => {},
}: {
    placeholder?: string;
    image?: string;
    onDragStart?: (e: any) => void;
    onDragEnter?: (e: any) => void;
    drop?: (e: any) => void;
    onDelete?: () => void;
    onChangeTitle?: (value: string) => void;
    onChangeIcon?: (value: string) => void;
    value?: string;
    icons?: { id: number; imageUrl: string }[];
}) => {
    const isOpenIconRef = useRef<HTMLObjectElement>(null);
    const [isOpenIcon, setIsOpenIcon] = useState<boolean>(false);

    const closeIcon = useCallback((e: any) => {
        if (isOpenIconRef.current && !isOpenIconRef.current.contains(e.target)) {
            setIsOpenIcon(false);
        }
    }, []);

    window.addEventListener("click", (e) => {
        window.addEventListener("click", closeIcon);
    });

    return (
        <div
            draggable
            onDragStart={onDragStart}
            onDragEnter={onDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={drop}
            css={css`
                display: flex;
                gap: 16px;
                align-items: center;
            `}
        >
            <Icon name="grip" color={theme.neutral400} hoverColor={theme.neutral500} />
            <div
                css={css`
                    display: flex;
                    gap: 12px;
                    flex: 1;
                `}
            >
                <div
                    ref={isOpenIconRef}
                    onClick={() => setIsOpenIcon(true)}
                    css={css`
                        width: 45px;
                        background-color: ${theme.neutral50};
                        border-radius: 10px;
                        align-items: center;
                        justify-content: center;
                        display: flex;
                        position: relative;

                        &:hover {
                            background-color: ${theme.neutral100};
                        }
                    `}
                >
                    <div
                        css={css`
                            background-image: url(${image});
                            background-size: contain;
                            background-repeat: no-repeat;
                            width: 24px;
                            height: 24px;
                        `}
                    />
                    {isOpenIcon ? (
                        <div
                            css={css`
                                box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.2);
                                display: grid;
                                grid-template-columns: 1fr 1fr 1fr 1fr;
                                position: absolute;
                                gap: 12px;
                                background-color: white;
                                padding: 24px;
                                border-radius: 12px;
                                position: absolute;
                                top: 55px;
                                left: 0;
                                z-index: 1;
                            `}
                        >
                            {icons?.map((item, index: number) => {
                                return (
                                    <IconWrapper key={index} onClick={() => onChangeIcon(item.imageUrl)}>
                                        <IconContainer image={item.imageUrl} />
                                    </IconWrapper>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                <InputField
                    value={value}
                    onChange={(value) => onChangeTitle(value)}
                    placeholder={placeholder}
                    styles={{
                        container: css`
                            flex: 1;
                        `,
                    }}
                />
            </div>

            <Icon name="delete" color={theme.neutral400} hoverColor={theme.neutral500} onClick={onDelete} />
        </div>
    );
};
